<template>
  <v-card class="w-100" variant="text">
    <slot name="header" />
    <div>
      <slot :object-collection="objectCollection" name="list">
        <BaseListTable
          :model-class="modelClass"
          :object-collection="objectCollection"
          :columns="columns"
          :pre-selected-rows="preSelectedRows"
          :unselectable-rows="unselectableRows"
          :detail-route-name="detailRouteName"
          :detail-extra-params="detailExtraParams"
          :custom-delete="customDelete"
          :has-actions="hasActions"
          :can-copy="canCopy"
          :can-add="canAdd"
          :can-delete="canDelete"
          :can-select="canSelect"
          :can-reorder="canReorder"
          :can-copy-data="canCopyData"
          :single-selection="singleSelection"
          :custom-actions="customActions"
          :has-detail-view="hasDetailView"
          :custom-detail="customDetail"
          :is-striped="isStriped"
          :has-expandable-row="props.hasExpandableRow"
          :error-message="errorMessage"
          @page-change="onPageChange"
          @sort="onSort"
          @error="handleError"
          @row-selection="onSelect"
          @base-list-event="(type) => emit('base-list-event', type)"
        >
          <template #top>
            <v-card v-if="props.canAdd" variant="flat" color="primary">
              <v-card-actions class="px-2 ga-2">
                <v-btn
                  as="router-link"
                  :to="addNewRoute"
                  variant="flat"
                  prepend-icon="mdi-plus"
                  >{{ prettyName }}</v-btn
                >
                <v-icon
                  v-if="helpMessage"
                  v-tooltip:right="helpMessage"
                  icon="mdi-help-circle-outline"
                  density="compact"
                />
              </v-card-actions>
            </v-card>
            <div v-if="showFilters" class="filters">
              <v-container class="ma-0">
                <v-row align-content="center">
                  <v-col cols="12" class="pa-0 d-flex ga-3 mx-6 my-2">
                    <slot name="filters-left" />
                    <ParentSelectorsV2 :model-class="modelClass" />
                    <SearchFields :model-class="modelClass" />
                    <slot name="filters" />
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </template>
          <template #detail="slotProps">
            <slot :row="slotProps.row" name="detail" />
          </template>
        </BaseListTable>
      </slot>
      <v-card
        v-if="errorMessages.length > 0"
        class="mb-12"
        color="red-darken-1"
      >
        <v-card-text
          v-for="(validationErrorMessage, index) in errorMessages"
          :key="index"
          class="text-medium-emphasis text-caption"
        >
          <div class="text-white text-left">
            {{ validationErrorMessage }}
          </div>
        </v-card-text>
      </v-card>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { ApiListSubscription } from '@/api/ApiClientV2'
import errorHandler from '@/api/ErrorHandler'
import { clientAppRouteName } from '@/apps/clientAppRegistry'
import { getClientAppOfRoute } from '@/apps/routingUtils'
import ParentSelectorsV2 from '@/components/common/lists/ParentSelectorsV2.vue'
import SearchFields from '@/components/common/lists/SearchFields.vue'
import { BaseListColumn, TransientBaseObjectType } from '@/models/core/base'
import { getObjectTypesUp } from '@/models/objectRegistry'
import routerHandler from '@/router/routerHandler'
import { objectFromMap } from '@/util/util'

import BaseListTable from './BaseListTable.vue'
import { CustomAction } from './interfaces'

interface Props {
  modelClass: TransientBaseObjectType
  customActions?: CustomAction[]
  preSelectedRows?: string[]
  unselectableRows?: string[]
  detailExtraParams?: any
  createRouteExtras?: any
  customColumns?: BaseListColumn[]
  hasActions?: boolean
  canAdd?: boolean
  canDelete?: boolean
  canCopy?: boolean
  canSelect?: boolean
  canReorder?: boolean
  singleSelection?: boolean
  loading?: boolean
  isStriped?: boolean
  hasExpandableRow?: boolean
  canCopyData?: boolean
  hasDetailView?: boolean
  detailRouteName?: string
  createRouteName?: string
  filterPrefix?: string
  customDelete?: any
  customDetail?: any
  collectionFilter?: any
  helpMessage?: any
  showFilters?: boolean
  errorMessage?: any
}

const props = withDefaults(defineProps<Props>(), {
  customColumns: null,
  customActions: () => [],
  preSelectedRows: () => [],
  unselectableRows: () => [],
  detailExtraParams: () => {
    return
  },
  createRouteExtras: () => {
    return
  },
  hasActions: true,
  canAdd: true,
  canDelete: true,
  canCopyData: true,
  canCopy: false,
  canSelect: false,
  canReorder: false,
  singleSelection: false,
  loading: false,
  isStriped: false,
  detailed: false,
  hasDetailView: true,
  detailRouteName: '',
  createRouteName: '',
  filterPrefix: '',
  customDelete: null,
  customDetail: null,
  collectionFilter: null,
  helpMessage: null,
  showFilters: true,
  errorMessage: () => {
    return {
      cantSelectRow: 'Row cannot be selected',
    }
  },
})

const emit = defineEmits<{
  (event: 'row-selection', selection: any[]): void
  (event: 'base-list-event', type: string): void
}>()

// Composables
const router = useRouter()
const route = useRoute()
const store = useStore()

// Refs
const columns = ref<BaseListColumn[]>(
  props.customColumns || props.modelClass.columns()
)
const errorMessages = ref<string[]>([])

// Constants
const prefix = computed(() =>
  props.filterPrefix ? `${props.filterPrefix}~` : ''
)
const objectType = computed(() => props.modelClass.objectType)

// Create collection instance
const objectCollection = ref<ApiListSubscription<
  typeof props.modelClass
> | null>(null)

/**
 * This function should check that the modelClass is properly configured with all required fields
 * so that the BaseList can work
 */
// Validation
function validateConfiguration() {
  const errors: string[] = []

  if (!props.modelClass) {
    errors.push('BaseListV2: The object class has not been defined.')
  }

  if (!objectType.value) {
    const className = (() => {
      try {
        return new props.modelClass().constructor.name
      } catch {
        return props.modelClass.constructor.name
      }
    })()
    errors.push(
      `BaseListV2: The object class ${className} has not defined its 'objectType'`
    )
  }

  if (objectType.value && !columns.value) {
    errors.push(
      `BaseList2: The object class '${objectType.value}' has not defined any columns!`
    )
  }

  if (errors.length) {
    router.replace({
      name: 'error',
      params: { errors: JSON.stringify(errors) },
    })
  }
}

// Update mounted hook
onMounted(() => {
  validateConfiguration()
  filterCollection()
})

const refreshCollection = async () => {
  if (objectCollection.value?.refresh) {
    clearErrors()
    await objectCollection.value.refresh()
  }
}

// Pagination and Sorting
function onPageChange(page: number) {
  routerHandler.setPage(prefix.value, page)
}

function onSort(sortItems: Array<{ key: string; order: 'asc' | 'desc' }>) {
  if (!sortItems.length) return

  const [{ key, order }] = sortItems
  const column = columns.value.find((col) => col.key === key)
  const sortField = column?.sortFieldName || key

  const query = {
    ...route.query,
    order_by: `${sortField}${order === 'desc' ? '_dsc' : ''}`,
  }

  routerHandler.setQuery(prefix.value, query)
}

// Route Generation
function getCreateRouteName(): string {
  if (props.createRouteName) return props.createRouteName

  const clientApp = getClientAppOfRoute(route)
  return clientApp
    ? clientAppRouteName(clientApp.view_id, `${objectType.value}-create`)
    : ''
}

function generateCreateRouteExtras() {
  const ancestors = [
    ...new Set([
      ...props.modelClass.ancestors.map(
        (ancestor) => ancestor.modelClass.objectType
      ),
      ...getObjectTypesUp(objectType.value),
    ]),
  ]

  const contextIds = store.getters['global/idsFromContext'](ancestors)
  const queryFromContext = objectFromMap(contextIds)

  const ancestorQueries = route.query.ancestor
    ? ancestors.reduce(
        (acc, ancestor) => ({
          ...acc,
          [ancestor]: route.query[ancestor],
        }),
        {}
      )
    : {}

  const queryResult = {
    ...queryFromContext,
    ...ancestorQueries,
    ...props.createRouteExtras,
  }

  return {
    query: queryResult,
  }
}

// Computed for new route
const addNewRoute = computed(() => {
  const routeQuery = {
    name: getCreateRouteName(),
    ...generateCreateRouteExtras(),
  }
  if (routeQuery.query.id === undefined) {
    routeQuery.query.id = '0'
  }
  return routeQuery
})

const onSelect = (selection) => {
  emit('row-selection', selection)
}

const handleError = (error) => {
  errorMessages.value = errorHandler.errorToStrings(error)
}

const prettyName = computed(() => {
  if (props.modelClass.prettyName) {
    return props.modelClass.prettyName()
  } else {
    return objectType.value.charAt(0).toUpperCase() + objectType.value.slice(1)
  }
})

async function filterCollection() {
  const filter = routerHandler.query(prefix.value)
  const pagination = routerHandler.pagination(prefix.value)

  const combinedFilter = {
    ...props.collectionFilter,
    ...filter,
  }

  function removeEmpty(obj: Record<string, any>) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== ''))
  }

  const validFilter = props.modelClass.isValidFilter(combinedFilter)
    ? removeEmpty(combinedFilter)
    : null

  if (!objectCollection.value) {
    objectCollection.value = props.modelClass.collection(
      validFilter,
      pagination
    )
  } else {
    await objectCollection.value.setFilterAndPagination(validFilter, pagination)
  }
}

function clearErrors() {
  errorMessages.value = []
}

// Watchers
watch(
  () => route.query,
  () => {
    filterCollection()
  },
  { deep: true }
)

watch(
  () => props.collectionFilter,
  () => {
    if (props.collectionFilter)
      routerHandler.setQuery(prefix.value, props.collectionFilter)
  },
  { deep: true }
)

defineExpose({ refreshCollection, handleError, clearErrors })
</script>

<style scoped>
.filters {
  margin: 0.5rem 0;
}

.filter-group {
  margin-right: 0.75rem;
  margin-bottom: 0.25rem;
}

.filter-group:last-child {
  margin-right: 0;
}
</style>
